import { useEffect, useMemo, useState } from "react";
import { navigateTo } from "../../router";
import { MemoryFeastBoardView } from "./MemoryFeastBoardView";
import { MEMORY_FEAST_REPLAY_FIXTURES } from "./replay-fixtures.generated";
import {
  buildMemoryFeastReplaySnapshots,
  type MemoryFeastReplaySnapshot,
} from "./replay-runner";
import "./styles/memory-feast-tokens.css";

interface ReplayScreenProps {
  readonly scenario: string;
}

const SPEED_OPTIONS = [
  { label: "느리게", ms: 1200 },
  { label: "보통", ms: 700 },
  { label: "빠르게", ms: 350 },
] as const;

const PHASE_LABELS: Readonly<Record<string, string>> = {
  place: "배치",
  "place-bonus": "보너스 배치",
  open: "오픈",
  "game-over": "종료",
};

function clampStepIndex(
  index: number,
  snapshots: readonly MemoryFeastReplaySnapshot[],
): number {
  if (snapshots.length === 0) return 0;
  return Math.min(Math.max(0, index), snapshots.length - 1);
}

function parseInteger(value: string | null): number | null {
  if (value === null || value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : null;
}

function resolveStepIndexFromSearch(
  snapshots: readonly MemoryFeastReplaySnapshot[],
): number {
  if (typeof window === "undefined" || snapshots.length === 0) return 0;
  const params = new URLSearchParams(window.location.search);
  const seq = parseInteger(params.get("seq"));
  if (seq !== null) {
    const index = snapshots.findIndex((s) => s.event.sequenceNumber === seq);
    if (index >= 0) return index;
  }
  const step = parseInteger(params.get("step"));
  if (step !== null) return clampStepIndex(step, snapshots);
  return 0;
}

function replaceStepQuery(
  index: number,
  snapshots: readonly MemoryFeastReplaySnapshot[],
): void {
  if (typeof window === "undefined" || snapshots.length === 0) return;
  const url = new URL(window.location.href);
  url.searchParams.set("step", String(index));
  url.searchParams.delete("seq");
  window.history.replaceState(null, "", `${url.pathname}${url.search}`);
}

function eventSummary(snapshot: MemoryFeastReplaySnapshot): string {
  const event = snapshot.event;
  if (event.eventType === "init") return `#${event.sequenceNumber} init`;
  const bowlIds = (event.payload as { bowlIds?: number[] }).bowlIds ?? [];
  const labels = bowlIds.map((id) => id + 1).join(", ");
  if (event.eventType === "OpenBowls") {
    const open = snapshot.state.lastOpen;
    const verdict = open?.matched ? "동수 — 승리!" : "이수 — 재덮기";
    return `#${event.sequenceNumber} 오픈 · 그릇 ${labels} · ${verdict}`;
  }
  return `#${event.sequenceNumber} 배치 · 그릇 ${labels}`;
}

export function ReplayScreen({ scenario }: ReplayScreenProps) {
  const fixture = MEMORY_FEAST_REPLAY_FIXTURES[scenario] ?? null;
  const snapshots = useMemo(
    () => (fixture ? buildMemoryFeastReplaySnapshots(fixture.events) : []),
    [fixture],
  );
  const namesByPlayerId = useMemo(
    () =>
      fixture
        ? Object.fromEntries(fixture.players.map((p) => [p.id, p.name]))
        : {},
    [fixture],
  );

  const [stepIndex, setStepIndex] = useState(() =>
    resolveStepIndexFromSearch(snapshots),
  );
  const [isPlaying, setIsPlaying] = useState(false);
  const [speedMs, setSpeedMs] = useState<number>(SPEED_OPTIONS[1].ms);
  const [showRecap, setShowRecap] = useState(false);

  const safeStepIndex = clampStepIndex(stepIndex, snapshots);
  const current = snapshots[safeStepIndex] ?? snapshots[0] ?? null;
  const maxIndex = snapshots.length - 1;

  useEffect(() => {
    setStepIndex(resolveStepIndexFromSearch(snapshots));
  }, [snapshots]);

  useEffect(() => {
    if (!isPlaying) return;
    if (safeStepIndex >= maxIndex) {
      setIsPlaying(false);
      return;
    }
    const timer = setTimeout(() => {
      const next = clampStepIndex(safeStepIndex + 1, snapshots);
      setStepIndex(next);
      replaceStepQuery(next, snapshots);
    }, speedMs);
    return () => clearTimeout(timer);
  }, [isPlaying, safeStepIndex, maxIndex, speedMs, snapshots]);

  function setReplayStep(index: number): void {
    const next = clampStepIndex(index, snapshots);
    setStepIndex(next);
    replaceStepQuery(next, snapshots);
  }

  if (!fixture || !current) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-primary p-6 text-text-primary">
        <section className="max-w-sm rounded-md border border-border-primary bg-surface-secondary p-6 text-center">
          <h1 className="mb-2 font-semibold text-lg">
            리플레이를 찾을 수 없습니다
          </h1>
          <p className="mb-4 text-sm text-text-secondary">{scenario}</p>
          <button
            type="button"
            className="rounded-md bg-accent-blue px-4 py-2 font-semibold text-sm text-white"
            onClick={() => navigateTo("/")}
          >
            닫기
          </button>
        </section>
      </div>
    );
  }

  const state = current.state;
  const isGameOver = state.phase === "game-over";
  const winnerName = state.winnerId ? namesByPlayerId[state.winnerId] : null;
  const turnName =
    namesByPlayerId[state.currentPlayerId] ?? state.currentPlayerId;
  const phaseLabel = PHASE_LABELS[state.phase] ?? state.phase;
  const phaseDetail = state.phase === "place" ? ` R${state.round}/9` : "";

  // Default: masked viewer projection (covered counts absent from DOM).
  // Recap toggle: canonical state for post-game review.
  const bowls = showRecap
    ? state.bowls.map((b) => ({ id: b.id, open: b.open, count: b.count }))
    : current.viewer.bowls;

  return (
    <div
      data-testid="memory-feast-replay-screen"
      className="min-h-screen px-3 py-4 sm:px-6"
      style={{ background: "var(--cn-memory-feast-bg)" }}
    >
      <div className="mx-auto flex max-w-xl flex-col gap-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="m-0 font-bold text-lg text-text-primary">
              기억의 만찬 리플레이
            </h1>
            <p className="mt-0.5 text-text-muted text-xs">
              {fixture.players.map((p) => p.name).join(" vs ")} · 시뮬레이션
            </p>
          </div>
          <button
            type="button"
            className="rounded-md border border-border-primary bg-surface-secondary px-3 py-1.5 text-text-secondary text-xs"
            onClick={() => navigateTo("/")}
          >
            ‹ 뒤로
          </button>
        </div>

        {isGameOver ? (
          <div
            data-testid="memory-feast-replay-result"
            className="rounded-md border border-accent-blue-light bg-accent-blue-bg px-4 py-3 text-center font-semibold text-accent-blue text-sm"
          >
            🏆 {winnerName ?? state.winnerId} 승리 — 동수 그릇쌍 발견
          </div>
        ) : (
          <div className="rounded-md border border-border-subtle bg-surface-secondary px-4 py-2 text-center text-sm text-text-secondary">
            페이즈:{" "}
            <span className="font-semibold">
              {phaseLabel}
              {phaseDetail}
            </span>{" "}
            · 차례 <span className="font-semibold">{turnName}</span>
          </div>
        )}

        <div className="flex justify-center">
          <MemoryFeastBoardView bowls={bowls} lastOpen={state.lastOpen} />
        </div>

        <div
          data-testid="memory-feast-replay-toolbar"
          className="flex flex-col gap-2 rounded-md border border-border-subtle bg-surface-secondary p-3"
        >
          <p
            data-testid="memory-feast-replay-event-summary"
            className="text-center text-sm text-text-primary"
          >
            {eventSummary(current)} ·{" "}
            <span className="text-text-muted">
              {namesByPlayerId[current.event.userId] ?? current.event.userId}
            </span>
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="memory-feast-replay-prev"
              disabled={safeStepIndex === 0}
              onClick={() => {
                setIsPlaying(false);
                setReplayStep(safeStepIndex - 1);
              }}
              className="rounded-md border border-border-primary bg-surface-primary px-3 py-2 font-semibold text-sm text-text-primary disabled:opacity-40"
            >
              ◀ 이전
            </button>
            <button
              type="button"
              data-testid="memory-feast-replay-play-toggle"
              disabled={safeStepIndex >= maxIndex}
              onClick={() => setIsPlaying((p) => !p)}
              className="rounded-md bg-accent-blue px-3 py-2 font-semibold text-sm text-white disabled:opacity-40"
            >
              {isPlaying ? "⏸ 일시정지" : "▶ 자동재생"}
            </button>
            <button
              type="button"
              data-testid="memory-feast-replay-next"
              disabled={safeStepIndex === maxIndex}
              onClick={() => {
                setIsPlaying(false);
                setReplayStep(safeStepIndex + 1);
              }}
              className="rounded-md border border-border-primary bg-surface-primary px-3 py-2 font-semibold text-sm text-text-primary disabled:opacity-40"
            >
              다음 ▶
            </button>
            <span
              data-testid="memory-feast-replay-step-count"
              className="ml-auto text-text-secondary text-xs"
            >
              {safeStepIndex + 1}/{snapshots.length}
            </span>
          </div>
          <input
            type="range"
            min={0}
            max={maxIndex}
            value={safeStepIndex}
            data-testid="memory-feast-replay-step-slider"
            onChange={(event) => {
              setIsPlaying(false);
              setReplayStep(Number(event.target.value));
            }}
            className="w-full"
          />
          <div className="flex items-center justify-between text-text-muted text-xs">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                data-testid="memory-feast-replay-recap-toggle"
                checked={showRecap}
                onChange={(event) => setShowRecap(event.target.checked)}
              />
              전체 공개 (복기)
            </label>
            <label className="flex items-center gap-2">
              재생 속도
              <select
                data-testid="memory-feast-replay-speed"
                value={speedMs}
                onChange={(event) => setSpeedMs(Number(event.target.value))}
                className="rounded-md border border-border-primary bg-surface-primary px-2 py-1 text-text-primary"
              >
                {SPEED_OPTIONS.map((option) => (
                  <option key={option.ms} value={option.ms}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}
